import type { BatchAttachmentInfo, TemplateLibraryLanguage, TemplatePair } from '@fluenceur/types';
import type { TemplateFormValues } from '@/components/templates/template-form-schema';

interface BuildSendTemplateSeedArgs {
  /** The (channel, context) pair derived from the active send mode. */
  pair: TemplatePair;
  /** Locale the message is composed in. */
  language: TemplateLibraryLanguage;
  /** Plain-text body currently composed in the editor. */
  content: string;
  /** Stringified Lexical JSON of the composed body, when available. */
  messageSnapshot: string | null;
  attachments?: BatchAttachmentInfo[];
}

/**
 * Build the seed values for the "save as template" form embedded in a send
 * flow. The pair, language and content are taken from the active composition so
 * the user only has to add a name. `isDefault` starts unchecked per spec.
 *
 * Parses the Lexical snapshot defensively: a malformed snapshot degrades to a
 * null structured body rather than throwing, so the plain-text content still
 * seeds the form.
 */
export const buildSendTemplateSeed = ({
  pair,
  language,
  content,
  messageSnapshot,
  attachments,
}: BuildSendTemplateSeedArgs): TemplateFormValues => ({
  name: '',
  channel: pair.channel,
  context: pair.context,
  language,
  content,
  contentLexicalJson: parseSnapshot(messageSnapshot),
  isDefault: false,
  ...(attachments ? { attachments } : {}),
});

const parseSnapshot = (snapshot: string | null): Record<string, unknown> | null => {
  if (!snapshot) {
    return null;
  }
  try {
    const parsed = JSON.parse(snapshot);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};
